import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_hasConvectionOvenAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasDishwasherAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasDoubleOvenAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasFreezerAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasGarbageDisposalAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasMicrowaveOvenAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasOvenAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasRangeAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasRefrigeratorAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasTrashCompactorAppliance,
  useRentalManagerUserPropertyEditWebApiHandler_hasWasherDryerHookupsAppliance,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { APPLIANCE_KEYS, APPLIANCE_METADATA, type ApplianceKey } from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<ApplianceKey, boolean>;

const AppliancesAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const applianceTypes = listingDetails?.buildingAttributes?.applianceTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasConvectionOven: applianceTypes?.hasConvectionOven ?? false,
    hasDishwasher: applianceTypes?.hasDishwasher ?? false,
    hasDoubleOven: applianceTypes?.hasDoubleOven ?? false,
    hasFreezer: applianceTypes?.hasFreezer ?? false,
    hasGarbageDisposal: applianceTypes?.hasGarbageDisposal ?? false,
    hasWasherDryerHookups: applianceTypes?.hasWasherDryerHookups ?? false,
    hasMicrowaveOven: applianceTypes?.hasMicrowaveOven ?? false,
    hasOven: applianceTypes?.hasOven ?? false,
    hasRange: applianceTypes?.hasRange ?? false,
    hasRefrigerator: applianceTypes?.hasRefrigerator ?? false,
    hasTrashCompactor: applianceTypes?.hasTrashCompactor ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Appliances updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateConvectionOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasConvectionOvenAppliance,
    skipToast: true,
    meta: { context: { api: 'hasConvectionOvenAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateDishwasher } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDishwasherAppliance,
    skipToast: true,
    meta: { context: { api: 'hasDishwasherAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateDoubleOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasDoubleOvenAppliance,
    skipToast: true,
    meta: { context: { api: 'hasDoubleOvenAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateFreezer } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasFreezerAppliance,
    skipToast: true,
    meta: { context: { api: 'hasFreezerAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateGarbageDisposal } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasGarbageDisposalAppliance,
    skipToast: true,
    meta: { context: { api: 'hasGarbageDisposalAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateWasherDryerHookups } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasWasherDryerHookupsAppliance,
    skipToast: true,
    meta: { context: { api: 'hasWasherDryerHookupsAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateMicrowaveOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasMicrowaveOvenAppliance,
    skipToast: true,
    meta: { context: { api: 'hasMicrowaveOvenAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateOven } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasOvenAppliance,
    skipToast: true,
    meta: { context: { api: 'hasOvenAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateRange } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasRangeAppliance,
    skipToast: true,
    meta: { context: { api: 'hasRangeAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateRefrigerator } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasRefrigeratorAppliance,
    skipToast: true,
    meta: { context: { api: 'hasRefrigeratorAppliance', details: { listingAlias } } },
  });

  const { mutateAsync: updateTrashCompactor } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTrashCompactorAppliance,
    skipToast: true,
    meta: { context: { api: 'hasTrashCompactorAppliance', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateConvectionOven> = {
    hasConvectionOven: updateConvectionOven,
    hasDishwasher: updateDishwasher,
    hasDoubleOven: updateDoubleOven,
    hasFreezer: updateFreezer,
    hasGarbageDisposal: updateGarbageDisposal,
    hasWasherDryerHookups: updateWasherDryerHookups,
    hasMicrowaveOven: updateMicrowaveOven,
    hasOven: updateOven,
    hasRange: updateRange,
    hasRefrigerator: updateRefrigerator,
    hasTrashCompactor: updateTrashCompactor,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as ApplianceKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = APPLIANCE_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Appliances</Legend>} disabled={isInputFormDisabled}>
      {APPLIANCE_KEYS.map((fieldName) => {
        const metadata = APPLIANCE_METADATA[fieldName];
        return (
          <LabeledControl
            key={`applianceTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(AppliancesAmenities);
